/**
 * Teclado virtual aberto?
 *
 * No navegador o teclado encolhe só o `visualViewport`; `innerHeight` fica.
 * No app Android (WebView com insets de teclado aplicados como padding) a
 * janela inteira encolhe junto, e `visualViewport.height === innerHeight`.
 * A comparação entre os dois nunca via o teclado no app, e o rodapé fixo e o
 * botão da Sofia subiam por cima do campo focado: na tela do nome, o toque no
 * campo caía neles, o campo perdia o foco e o teclado fechava (gravação
 * Hotjar 118979682302, 110 toques em 17 s).
 *
 * Para o caso do app, comparamos `innerHeight` com a maior altura já vista na
 * mesma largura, e só com um campo de texto focado — redimensionar a janela
 * no desktop não conta como teclado.
 */

const BROWSER_KEYBOARD_INSET_PX = 80;
const RESIZED_KEYBOARD_MIN_PX = 120;
export const SOFT_KEYBOARD_CLASS = 'soft-keyboard-open';
export const SOFT_KEYBOARD_EVENT = 'cm:soft-keyboard';

const NON_TEXT_INPUT_TYPES = new Set([
  'button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit',
]);

const maxHeightByWidth = new Map();

export function isTextEntryElement(el) {
  if (!el || typeof el !== 'object') return false;
  const tag = String(el.tagName || '').toLowerCase();
  if (tag === 'textarea') return !el.readOnly && !el.disabled;
  if (tag === 'input') {
    const type = String(el.type || 'text').toLowerCase();
    return !NON_TEXT_INPUT_TYPES.has(type) && !el.readOnly && !el.disabled;
  }
  return !!el.isContentEditable;
}

function rememberHeight() {
  const w = window.innerWidth || 0;
  const h = window.innerHeight || 0;
  const prev = maxHeightByWidth.get(w) || 0;
  if (h > prev) maxHeightByWidth.set(w, h);
  return { h, max: Math.max(prev, h) };
}

export function detectSoftKeyboardOpen() {
  if (typeof window === 'undefined') return false;
  const { h, max } = rememberHeight();
  const vv = window.visualViewport;
  if (vv && vv.height < h - BROWSER_KEYBOARD_INSET_PX) return true;
  return h < max - RESIZED_KEYBOARD_MIN_PX
    && isTextEntryElement(typeof document !== 'undefined' ? document.activeElement : null);
}

let installed = false;
let lastOpen = false;

// O `scrollIntoView` que os formulários fazem no foco roda antes de a janela
// do app terminar de encolher; o campo acaba colado na borda de baixo.
function keepFocusedFieldVisible() {
  const el = document.activeElement;
  if (!isTextEntryElement(el) || typeof el.getBoundingClientRect !== 'function') return;
  const r = el.getBoundingClientRect();
  // No WebView o `visualViewport` atualiza depois do `innerHeight`.
  const visibleBottom = Math.min(
    window.innerHeight || Infinity,
    window.visualViewport ? window.visualViewport.height : Infinity,
  );
  if (r.top >= 0 && r.bottom <= visibleBottom - BROWSER_KEYBOARD_INSET_PX) return;
  try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
}

/** Mantém `html.soft-keyboard-open` e avisa por `cm:soft-keyboard`. Idempotente. */
export function installSoftKeyboardWatcher() {
  if (installed || typeof window === 'undefined' || typeof document === 'undefined') return;
  installed = true;
  let raf = null;
  const update = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = null;
      const open = detectSoftKeyboardOpen();
      if (open === lastOpen) return;
      lastOpen = open;
      document.documentElement.classList.toggle(SOFT_KEYBOARD_CLASS, open);
      if (open) {
        keepFocusedFieldVisible();
        setTimeout(() => { if (lastOpen) keepFocusedFieldVisible(); }, 300);
      }
      try {
        window.dispatchEvent(new CustomEvent(SOFT_KEYBOARD_EVENT, { detail: { open } }));
      } catch {}
    });
  };
  update();
  window.visualViewport?.addEventListener('resize', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  window.addEventListener('orientationchange', update, { passive: true });
  document.addEventListener('focusin', update, true);
  document.addEventListener('focusout', update, true);
}

export function isSoftKeyboardOpen() {
  return lastOpen;
}

/** Só para testes. */
export function __resetSoftKeyboardForTests() {
  maxHeightByWidth.clear();
  installed = false;
  lastOpen = false;
}
